/**
 * Bazaarvoice "Read Reviews" scroll bridge (E20-1391)
 *
 * Bazaarvoice's buy-box rating summary already scrolls to the Reviews section on its own
 * once a Reviews container is on the page — but it scrolls the section to the very top of
 * the viewport, leaving the heading buried under the sticky header and announcement bar.
 * This intercepts the click and re-runs the scroll with the theme's header offset.
 *
 * A capture-phase listener on the document runs before BV's own handler, which is bound to
 * the buttons themselves, so BV's scroll can be stopped before it happens. The offset,
 * focus move, and reduced-motion handling are delegated to bfx-anchor-scroll (E20-1160) so
 * that behavior stays defined in one place.
 *
 * Scoped to `.product-block--bazaarvoice`, the buy-box wrapper in product-template.liquid.
 * Product cards ("You may also like", mini-compare) render their own rating summaries and
 * must keep BV's default behavior — scrolling those to the current product's reviews would
 * be wrong.
 */
const initBazaarvoiceScroll = () => {
  const SUMMARY = '.product-block--bazaarvoice';
  const REVIEWS = '[data-bv-show="reviews"]';

  if (!document.querySelector(SUMMARY)) return;

  const anchorScroll = window.bfxAnchorScroll;
  if (!anchorScroll) {
    console.warn(
      'bfx-bazaarvoice-scroll: bfx-anchor-scroll.js is not loaded — leaving Bazaarvoice to handle the scroll.'
    );
    return;
  }

  // The Reviews container ships empty on every PDP and stays that way if BV's display
  // module isn't deployed for the account's zone, so height — not presence — is the signal
  // that there is something to scroll to.
  const renderedReviews = () => {
    const reviews = document.querySelector(REVIEWS);
    return reviews?.getBoundingClientRect().height > 0 ? reviews : null;
  };

  document.addEventListener(
    'click',
    (event) => {
      if (!(event.target instanceof Element)) return;
      // Let the browser handle modified / non-primary clicks.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;
      if (!event.target.closest(SUMMARY)) return;

      const reviews = renderedReviews();
      if (!reviews) return; // Nothing rendered to scroll to — leave BV's behavior alone.

      event.preventDefault();
      event.stopPropagation();
      // Instant, not smooth: the Bazaarvoice widget starves the main thread, so an animated
      // scroll sits still for ~2.8s before jumping. Measured on the same PDP with and
      // without BV loaded — without it, a smooth scroll starts inside 160ms.
      anchorScroll.scrollToTarget(reviews, false, 'auto');
    },
    true
  );
};

initBazaarvoiceScroll();
